import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { ArrowRight, ArrowLeft, Calendar, User, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
  const { slug } = useParams();
  const [news, setNews] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [featuredNews, setFeaturedNews] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchArticle();
    } else {
      fetchNews();
    }
  }, [slug, currentPage]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/news/${slug}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news?page=${currentPage}&per_page=6`);
      const articles = response.data.articles || [];
      
      // Find featured article
      const featured = articles.find(a => a.featured);
      if (featured && currentPage === 1) {
        setFeaturedNews(featured);
        setNews(articles.filter(a => a.id !== featured.id));
      } else {
        setFeaturedNews(null);
        setNews(articles);
      }
      
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  // Single Article View
  if (slug && article) {
    return (
      <div>
        <section className="bg-primary py-16" data-testid="news-article-hero">
          <div className="container-custom">
            <Link to="/news" className="inline-flex items-center text-white/80 hover:text-white mb-6" data-testid="news-back-link">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to News
            </Link>
            <div className="max-w-3xl">
              <span className="inline-block px-3 py-1 bg-accent text-white text-sm rounded-full mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-white/80">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(article.created_at), 'MMM dd, yyyy')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-spacing" data-testid="news-article-content">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              {article.image && (
                <div className="aspect-video rounded-2xl overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="prose prose-lg max-w-none">
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {article.content}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-warm" data-testid="news-article-cta">
          <div className="container-custom text-center">
            <h2 className="font-heading text-2xl font-bold text-primary mb-4">
              Stay Updated with Our Latest News
            </h2>
            <Link to="/news" data-testid="news-view-all">
              <Button className="btn-primary">
                View All News
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // News List View
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-16 md:py-24" data-testid="news-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="text-accent font-medium mb-2">News & Updates</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Latest News & Articles
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Stay informed about our latest updates, health tips, community programs, and hospital news.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredNews && (
        <section className="section-spacing bg-warm" data-testid="news-featured">
          <div className="container-custom">
            <Card className="overflow-hidden">
              <div className="grid lg:grid-cols-2">
                <div className="aspect-video lg:aspect-auto overflow-hidden">
                  <img
                    src={featuredNews.image || 'https://images.unsplash.com/photo-1579165466741-7f35e4755660?w=800'}
                    alt={featuredNews.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 bg-accent text-white text-sm rounded-full w-fit mb-4">
                    Featured
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-4">
                    {featuredNews.title}
                  </h2>
                  <p className="text-slate-600 mb-6">{featuredNews.excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{featuredNews.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{format(new Date(featuredNews.created_at), 'MMM dd, yyyy')}</span>
                    </div>
                  </div>
                  <Link to={`/news/${featuredNews.slug}`} data-testid="news-featured-read-more">
                    <Button className="btn-primary w-fit">
                      Read More
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* News Grid */}
      <section className="section-spacing" data-testid="news-grid">
        <div className="container-custom">
          <h2 className="font-heading text-2xl font-bold text-primary mb-8">
            {featuredNews ? 'Recent Articles' : 'All Articles'}
          </h2>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="card-service animate-pulse">
                  <div className="aspect-video bg-slate-200 rounded-t-2xl" />
                  <div className="p-6">
                    <div className="h-4 bg-slate-200 rounded w-1/4 mb-3" />
                    <div className="h-6 bg-slate-200 rounded w-full mb-3" />
                    <div className="h-4 bg-slate-200 rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : news.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No news articles found.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.map((article, index) => (
                <Card key={article.id} className="card-service overflow-hidden" data-testid={`news-card-${index}`}>
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image || 'https://images.unsplash.com/photo-1579165466741-7f35e4755660?w=800'}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-4 h-4 text-accent" />
                      <span className="text-xs font-medium text-accent uppercase tracking-wider">{article.category}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-primary text-lg mb-3 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2 mb-4">{article.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        {format(new Date(article.created_at), 'MMM dd, yyyy')}
                      </span>
                      <Link to={`/news/${article.slug}`} className="inline-flex items-center text-primary hover:text-accent font-medium text-sm transition-colors">
                        Read More <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-12" data-testid="news-pagination">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="news-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="news-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
